import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { useBusinessDay } from '../../../../shared/hooks/useBusinessDay.js';

/**
 * M11 L11.04 — Closure Signoff / Certificate viewer.
 *
 * Lists the full version chain of certificates for a verification,
 * surfaces the latest hash + integrity check, allows
 * regeneration with a reason. The "Re-issue" action requires the
 * caller to hold M11_SIGNOFF_REGENERATE; the server enforces that
 * — this page just shows the control.
 */
interface SignoffRow {
  readonly signoffId: string;
  readonly version: number;
  readonly certificateNumber: string;
  readonly issuedAt: string;
  readonly pdfHash: string;
  readonly pdfStorageUri: string | null;
  readonly regenerateReason: string | null;
  readonly prevT136Id: string | null;
}

interface VerifyResponse {
  readonly ok: boolean;
  readonly expected: string;
  readonly actual: string;
}

export function ClosureSignoffPage(): ReactNode {
  const { t } = useTranslation();
  // Dates follow the tenant's DISPLAY_DATE_FORMAT, not the reader's language picker.
  const { at } = useBusinessDay();
  const { verificationId = '' } = useParams<{ verificationId: string }>();
  const { token } = useAuth();
  const [chain, setChain] = useState<readonly SignoffRow[] | null>(null);
  const [latest, setLatest] = useState<SignoffRow | null>(null);
  const [verify, setVerify] = useState<VerifyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [reason, setReason] = useState<string>('');
  const [busy, setBusy] = useState<boolean>(false);

  const load = useCallback(async () => {
    try {
      const list = await apiRequest<readonly SignoffRow[]>(
        `/v1/closure/signoff/verification/${encodeURIComponent(verificationId)}`,
        { token },
      );
      setChain(list);
      const head = list[0] ?? null; // backend orders by version DESC
      setLatest(head);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, [token, verificationId]);

  useEffect(() => {
    if (!verificationId) return;
    void load();
  }, [verificationId, load]);

  const generate = async () => {
    setBusy(true);
    setInfo(null);
    try {
      await apiRequest(
        `/v1/closure/signoff/${encodeURIComponent(verificationId)}/generate`,
        { method: 'POST', token, body: {} },
      );
      setInfo('Certificate generated');
      await load();
    } catch (e) {
      setError(asMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    setBusy(true);
    setInfo(null);
    try {
      await apiRequest(
        `/v1/closure/signoff/${encodeURIComponent(verificationId)}/regenerate`,
        { method: 'POST', token, body: { reason } },
      );
      setInfo('Certificate re-issued');
      setReason('');
      await load();
    } catch (e) {
      setError(asMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const doVerify = async () => {
    if (!latest) return;
    setBusy(true);
    setInfo(null);
    try {
      const r = await apiRequest<VerifyResponse>(
        `/v1/closure/signoff/${encodeURIComponent(latest.signoffId)}/verify`,
        { token },
      );
      setVerify(r);
    } catch (e) {
      setError(asMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (error && !chain) {
    return (
      <main aria-label="closure-signoff">
        <p role="alert">{error}</p>
      </main>
    );
  }
  if (!chain) {
    return (
      <main aria-label="closure-signoff">
        <p>{t('closure.signoff.loading')}</p>
      </main>
    );
  }

  return (
    <main aria-label="closure-signoff">
      <h1>{t('closure.signoff.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {info && <p role="status">{info}</p>}

      {chain.length === 0 ? (
        <section aria-label="no-cert">
          <p aria-label="empty">{t('closure.signoff.empty')}</p>
          <button type="button" aria-label="generate" disabled={busy} onClick={() => void generate()}>
            {t('closure.signoff.generate')}
          </button>
        </section>
      ) : (
        <>
          <section aria-label="latest-cert">
            <h2>{t('closure.signoff.latest')}</h2>
            <p>
              {t('closure.signoff.certNo')} <span aria-label="cert-no">{latest?.certificateNumber}</span>
            </p>
            <p>
              {t('closure.signoff.version')} <span aria-label="cert-version">{latest?.version}</span>
            </p>
            <p>
              {t('closure.signoff.issued')} <span aria-label="cert-issued">{at(latest?.issuedAt)}</span>
            </p>
            <p>
              {t('closure.signoff.hash')} <code aria-label="cert-hash">{latest?.pdfHash}</code>
            </p>
            <button type="button" aria-label="verify" disabled={busy} onClick={() => void doVerify()}>
              {t('closure.signoff.verify')}
            </button>
            {verify && (
              <p aria-label="verify-result">
                {verify.ok ? t('closure.signoff.match') : t('closure.signoff.mismatch')}
              </p>
            )}
          </section>

          <section aria-label="regenerate">
            <h2>{t('closure.signoff.reissueH')}</h2>
            <label htmlFor="regen-reason">{t('closure.signoff.reason')}</label>
            <input
              id="regen-reason"
              aria-label="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            <button
              type="button"
              aria-label="regenerate"
              disabled={busy || reason.trim().length < 5}
              onClick={() => void regenerate()}
            >
              {t('closure.signoff.reissue')}
            </button>
          </section>

          <section aria-label="chain">
            <h2>{t('closure.signoff.chainH')}</h2>
            <table aria-label="chain-table">
              <thead>
                <tr>
                  <th>{t('closure.signoff.col.version')}</th>
                  <th>{t('closure.signoff.col.certNo')}</th>
                  <th>{t('closure.signoff.col.issued')}</th>
                  <th>{t('closure.signoff.col.hash')}</th>
                  <th>{t('closure.signoff.col.reason')}</th>
                </tr>
              </thead>
              <tbody>
                {chain.map((row) => (
                  <tr key={row.signoffId} aria-label={`chain-${row.version}`}>
                    <td>{row.version}</td>
                    <td>{row.certificateNumber}</td>
                    <td>{at(row.issuedAt)}</td>
                    <td>
                      <code>{row.pdfHash.slice(0, 12)}…</code>
                    </td>
                    <td>{row.regenerateReason ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}
    </main>
  );
}

function asMsg(e: unknown): string {
  if (e instanceof ApiError) {
    if (typeof e.body === 'object' && e.body !== null && 'userMessage' in e.body) {
      return String((e.body as { userMessage: unknown }).userMessage);
    }
    return e.message;
  }
  return (e as Error).message;
}
